import { FC, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import Modal from 'components/layouts/modal';
import useApi from 'utils/use-api';
import wrapClick from 'utils/wrap-click';
import { StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';

interface ReviewModalProps {
  open: boolean;
  setOpen: (val: boolean) => void;
  requestId: number;
  assigneeName: string;
  onSuccess: () => void;
}

const ReviewModal: FC<ReviewModalProps> = ({ open, setOpen, requestId, assigneeName, onSuccess }) => {
  const api = useApi();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [reviewText, setReviewText] = useState('');

  const submitReviewMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/access-requests/${requestId}/review`, {
        rating,
        reviewText: reviewText.trim() || undefined,
      });
      return response.data;
    },
    onSuccess: () => {
      setRating(0);
      setReviewText('');
      setOpen(false);
      onSuccess();
    },
  });

  const handleSubmit = () => {
    if (rating === 0) {
      return;
    }
    submitReviewMutation.mutate();
  };

  const renderActions = () => {
    return (
      <div className="flex gap-x-3">
        <button
          type="button"
          onClick={wrapClick(() => setOpen(false))}
          disabled={submitReviewMutation.isPending}
          className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Skip for Now
        </button>
        <button
          type="button"
          onClick={wrapClick(handleSubmit)}
          disabled={rating === 0 || submitReviewMutation.isPending}
          className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitReviewMutation.isPending ? 'Submitting...' : 'Submit Review'}
        </button>
      </div>
    );
  };

  return (
    <Modal open={open} setOpen={setOpen} title="Rate the Service" description={`How would you rate ${assigneeName}?`} size="3xl" renderActions={renderActions}>
      <div className="space-y-6 mt-4">
        <div className="flex flex-col items-center space-y-4">
          <div className="flex items-center gap-x-2">
            {[1, 2, 3, 4, 5].map(star => {
              const isFilled = star <= (hoveredRating || rating);
              return (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-110 focus:outline-none"
                >
                  {isFilled ? (
                    <StarIcon className="h-12 w-12 text-yellow-400" />
                  ) : (
                    <StarOutlineIcon className="h-12 w-12 text-gray-300" />
                  )}
                </button>
              );
            })}
          </div>
          {rating > 0 && (
            <p className="text-sm font-medium text-gray-700">
              {rating === 1 && 'Poor'}
              {rating === 2 && 'Fair'}
              {rating === 3 && 'Good'}
              {rating === 4 && 'Very Good'}
              {rating === 5 && 'Excellent'}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="reviewText" className="block text-sm font-medium text-gray-700 mb-2">
            Additional Comments (Optional)
          </label>
          <textarea
            id="reviewText"
            value={reviewText}
            onChange={e => setReviewText(e.target.value)}
            placeholder="Share more about your experience..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
          <p className="mt-2 text-sm text-gray-500">Your feedback helps us track service quality and improve our team's performance.</p>
        </div>

        {submitReviewMutation.isError && (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">
              {(submitReviewMutation.error as any)?.response?.data?.message || 'Failed to submit review. Please try again.'}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ReviewModal;
